import { Box, SimpleGrid, useBreakpointValue } from "@chakra-ui/react"
import dynamic from "next/dynamic"
import { useRouter } from "next/router"
import { useEffect, useRef, useState } from "react"
import { fhAnalyticsUserActions, pages } from "../../../../common/constants/constants"
import { searchDefaults, tabs } from "../../../../common/constants/search-constants"
import useIntersectionObserver from "../../../../common/hooks/useIntersectionObserver"
import sendFhAnalytics from "../../../../common/lib/fredhopper"
import { Tab } from "../../../../common/types/global"
import { FhResponseData } from "../../../../common/types/search"
import { redirectToPage } from "../../../../common/utils/router-utils"
import ResultsSparePartRow from "../../../shared/ResultsSparePartRow"
import ScrollShadow from "../../../shared/ScrollShadow"
import PageLoader from "./PageLoader/PageLoader"
import ResultsProductTile from "./ResultsProductTile/ResultsProductTile"
const Loader = dynamic(() => import("../../../shared/Loader"), { ssr: false, loading: () => <Box mt="12" /> })

type Props = {
  selectedTab: Tab
  items: FhResponseData[]
  pageNumber: number
  setPageNumber: (pageNumber: number | ((prevPageNumber: number) => number)) => void
  typeSelector: FhResponseData
  isLoading: boolean
  implicitRanges: FhResponseData
  searchTerm: string
  rid: string
  selectedColors: string | null
}

const ResultsWrapper = ({
  selectedTab,
  items,
  pageNumber,
  setPageNumber,
  typeSelector,
  isLoading,
  implicitRanges,
  searchTerm,
  rid,
  selectedColors,
}: Props) => {
  const router = useRouter()
  const [showLoadMoreSpacer, setShowLoadMoreSpacer] = useState<boolean>(true)
  /* Observer to show/hide the shadow when there are results to be scrolled down in the page */
  const shadowTriggerRef = useRef(null)
  const shadowEntry = useIntersectionObserver(shadowTriggerRef, {})
  const isShadowVisible = !shadowEntry?.isIntersecting

  // Total items in the selection for the selected tab
  const itemsCountTotal =
    typeSelector?.filtersection?.find((f: FhResponseData) => f.value.value === selectedTab)?.nr ?? 0

  const remainingItems = itemsCountTotal - (items?.length ?? 0)
  const isInfiniteScroll = pageNumber < searchDefaults.START_PAGE + searchDefaults.INFINITE_SCROLL_PAGES

  const page = selectedTab === tabs.PRO ? pages.PRODUCT : pages.SPARE_PART

  /* Responsive styles for border for product results grid */
  const numberOfGridColumns = useBreakpointValue({ base: 1, sm: 2, md: 3, lg: 4 })
  const incompleteRowItems = items.length % numberOfGridColumns
  const numberOfItemsOnLastRow = incompleteRowItems ? incompleteRowItems : numberOfGridColumns
  const productItemsBorderStyle = {
    [`& >:nth-of-type(${numberOfGridColumns}n)`]: { borderRight: "none" },
    [`& >:nth-last-of-type(-n+${numberOfItemsOnLastRow})`]: { borderBottom: "none" },
  }

  useEffect(() => {
    if (isLoading && remainingItems <= 0) setTimeout(() => setShowLoadMoreSpacer(false), 200)

    if (remainingItems > 0) setShowLoadMoreSpacer(true)
  }, [isLoading, remainingItems])

  const handleLoadMore = () => {
    setPageNumber((prevPageNumber: number) => prevPageNumber + 1)
  }

  const handleTileClick = (e: React.MouseEvent<HTMLElement>, itemId: string, pdpLinkQueryParamsUri = "") => {
    const isLeftButtonClicked = e.button === 0
    const isRightButtonClicked = e.button === 2
    const isCtrlOrMetaKeyPressed = e.metaKey || e.ctrlKey
    const openInSameTab = isLeftButtonClicked && !isCtrlOrMetaKeyPressed

    if (isRightButtonClicked) return false
    if (openInSameTab) e.preventDefault()

    //wait until analytics is sent as otherwise analytics call is cancelled
    sendFhAnalytics({
      locale: router.locale,
      action: fhAnalyticsUserActions[`${selectedTab}CLICK`],
      sourceId: rid,
      itemId: itemId,
      shouldBeInterrupted: openInSameTab ? true : false,
    }).finally(() => {
      if (openInSameTab) redirectToPage(router, page, itemId, pdpLinkQueryParamsUri)
    })
  }

  return (
    <Box as="section" mt="6">
      {selectedTab === tabs.PRO && (
        <SimpleGrid
          templateColumns={{ base: "repeat(1, 1fr)", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)", lg: "repeat(4, 1fr)" }}
          sx={productItemsBorderStyle}
        >
          {items.map((item: FhResponseData, index: number) => {
            return (
              <ResultsProductTile
                key={item.id}
                product={item}
                implicitRanges={implicitRanges}
                searchTerm={searchTerm}
                handleTileClick={handleTileClick}
                index={index}
                selectedColors={selectedColors}
              />
            )
          })}
        </SimpleGrid>
      )}
      {selectedTab === tabs.SPT && (
        <Box>
          {items.map((item: FhResponseData) => (
            <ResultsSparePartRow
              key={item.id}
              sparePart={item}
              handleTileClick={handleTileClick}
              isFhData={true}
              searchTerm={searchTerm}
            />
          ))}
        </Box>
      )}
      {/* Dummy div used for observing the end of the rendered items */}
      <Box ref={shadowTriggerRef} />

      {/* Scroll shadow at the bottom of the list with items when there are more items to be scrolled */}
      <ScrollShadow isVisible={isShadowVisible} />

      {/* Logic to handle infinite loading and load on button click mix */}
      {isLoading ? (
        <Loader mt="12" />
      ) : remainingItems > 0 && items.length > 0 ? (
        <PageLoader
          selectedTab={selectedTab}
          remainingItems={remainingItems}
          handleLoadMore={handleLoadMore}
          isInfiniteScroll={isInfiniteScroll}
        />
      ) : (
        showLoadMoreSpacer && <Box h="18" />
      )}
    </Box>
  )
}

export default ResultsWrapper
